import React, { useState } from 'react';
import { ReactComponent as ChatPaymentBadge } from '@assets/images/app/chat-and-payment.svg';
import { ReactComponent as DirectPaymentBadge } from '@assets/images/app/direct-payment.svg';
import { ReactComponent as CircleDown } from '@assets/images/app/icon-circle-down.svg';
import { ReactComponent as CircleUp } from '@assets/images/app/icon-circle-up.svg';
import { useAuthentication } from '@hooks/authentication';
import { ProductModel } from '@models/product';
import { discountedAmountBy } from '@utils/common';
import { BenefitsBanner } from './BenefitsBanner';
import { PurchaseBenefits } from './PurchaseBenefits';

export const ProductSummary: React.FC<{ data: ProductModel; closed?: boolean }> = (props) => {
  const { authStore } = useAuthentication();
  const [showPointBenefits, setShowPointBenefits] = useState<boolean>(false);
  const data = props.data;
  const isLogin = !!authStore.user?.id;

  const excludeDiscount = (data?.periodDiscountExcludeChannels || []).includes('app') || false;
  if (data?.applyPeriodDiscount && data?.discountInfo?.discountedFee && !excludeDiscount) {
    const today = new Date();
    if (
      data?.discountInfo?.discountStartDate &&
      data?.discountInfo?.discountEndDate &&
      new Date(data?.discountInfo?.discountStartDate) < today &&
      new Date(data?.discountInfo?.discountEndDate) > today
    ) {
      data.fee = data?.discountInfo?.discountedFee || 0;
      data.discount = `${Math.round(100 - (data.fee / data?.originFee) * 100)}%`;
    }
  }

  const amount = data?.fee || 0;
  const expectedFee = discountedAmountBy(data?.fee || 0, 0);
  const purchasePoint = Math.floor(expectedFee * 0.005);
  const photoReviewPoint = Math.floor(expectedFee * 0.03);

  return (
    <div style={{ padding: '24px 16px' }}>
      <div>
        {data.saleType === 'class' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: 'var(--Primary-95, #FF3D8F)',
              fontFamily: 'Pretendard',
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: 400,
              marginBottom: '12px',
            }}>
            <ChatPaymentBadge />
            <div style={{ width: '1px', height: '10px', background: '#FFC5DD' }}></div>
            <div>희망 옵션 상담 후 구매 가능</div>
          </div>
        )}
        {data.saleType === 'commerce' && (
          <div
            style={{
              display: 'flex',
              gap: '6px',
              color: '#7950EF',
              fontFamily: 'Pretendard',
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: 400,
              marginBottom: '12px',
            }}>
            <DirectPaymentBadge />
            <div style={{ width: '1px', height: '10px', background: '#D5C5FF' }}></div>
            <div>희망 옵션 바로 구매 가능</div>
          </div>
        )}
      </div>
      <div
        style={{
          marginTop: '12px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flex: '1px',
        }}>
        <div
          style={{
            color: 'var(--Neutral-90, #424242)',
            fontFamily: 'Pretendard',
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 400,
          }}>
          {data?.title}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: '8px',
        }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          {data?.discount && data.discount.replace(/[^0-9]/g, '') !== '' && parseInt(data.discount) > 0 && (
            <div
              style={{
                color: props.closed ? '#B9BBBE' : 'var(--Primary-95, #FF3D8F)',
                fontFamily: 'Pretendard',
                fontSize: '22px',
                fontStyle: 'normal',
                fontWeight: 700,
                marginRight: '4px',
              }}>
              {data?.discount}
            </div>
          )}
          <div
            style={{
              fontFamily: 'Pretendard',
              fontSize: '22px',
              fontWeight: 'bold',
              color: props.closed ? '#B9BBBE' : '#242424',
            }}>
            {amount.toLocaleString()}
          </div>
          <div
            style={{
              fontFamily: 'Pretendard',
              fontSize: '14px',
              fontWeight: 'normal',
              color: props.closed ? '#B9BBBE' : '#242424',
              lineHeight: '26px',
            }}>
            원
            {/* {data?.showRegularTime && data?.regularTime && data?.regularTime > 0 ? ` / ${data?.regularTime}회` : <></>} */}
          </div>
          {data?.originFee && data.originFee > 0 && data.originFee !== data.fee ? (
            <div
              style={{
                fontFamily: 'Pretendard',
                marginLeft: '4px',
                textDecorationLine: 'line-through',
                color: props.closed ? '#B9BBBE' : '#b9bbbe',
                fontSize: '14px',
              }}>
              {`${data.originFee.toLocaleString()}원`}
            </div>
          ) : (
            <></>
          )}
          <button
            className="link-button"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: 'var(--Primary-95, #FF3D8F)',
              textAlign: 'right',
              fontFamily: 'Pretendard',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '20px',
              marginLeft: '4px',
              padding: '0px',
            }}
            onClick={() => {
              setShowPointBenefits((value) => !value);
            }}>
            <div>최대 적립</div>
            <div style={{ fontWeight: 700 }}>{(purchasePoint + photoReviewPoint).toLocaleString()}P</div>
            {showPointBenefits ? <CircleUp /> : <CircleDown />}
          </button>
        </div>
      </div>
      {props.closed && (
        <div
          style={{
            marginTop: '12px',
            color: 'var(--Negative-40, #FC4242)',
            fontFamily: 'Pretendard',
            fontSize: '18px',
            fontStyle: 'normal',
            fontWeight: 400,
          }}>
          다음 오픈을 기다려주세요.
        </div>
      )}
      {showPointBenefits && <PurchaseBenefits data={data} style={{ marginTop: '12px' }} collapse={!isLogin} />}
      {!isLogin && <BenefitsBanner />}
    </div>
  );
};
